import { apiFetch } from "./client";

/** Jedna izmerena veličina: ovaj period, isti raspon godinu ranije, i njihov odnos. */
export interface Measure {
  current: number;
  /**
   * Ista veličina godinu ranije, ili **null** kad prethodni period NIJE UVEZEN.
   *
   * Razlika je bitna: nula kaže da prošle godine nije bilo prodaje, null kaže da portal prošlu
   * godinu ne drži. Prodajni ledger počinje 01.01.2023, pa za 2023. nema nijedan red iz 2022 —
   * kolona nula tamo tvrdi nešto što niko nije rekao.
   */
  previous: number | null;
  /** `null` kad prošle godine nema šta da se poredi — izveštaj na tom mestu piše „/". */
  index: number | null;
}

/**
 * Količina u jedinici koja nije kilogram.
 *
 * Drži se ODVOJENO od tonaže, ne sabira se u nju. Sabrati 18.800 jednodnevnih pilića sa
 * kilogramima daje broj koji je pogrešan na način koji se ne vidi — a to štampani izveštaj radi.
 */
/**
 * Mera koja je ODNOS, pa sme da bude neizmerljiva.
 *
 * Razlika prema `Measure` je u `current`: zbir nad nijednim redom jeste nula (nije prodato ništa),
 * a odnos nad nijednim redom je **null**. Grupa prodata samo u komadima ne sme da dobije maržu po
 * kilogramu od 0,00 — to se čita kao „na kilogramu nije zarađeno", a kilograma nije bilo.
 */
export interface Ratio {
  current: number | null;
  previous: number | null;
  index: number | null;
}

export interface UnitQuantity {
  unit: string;
  current: number;
  /** **null** kad prethodna godina nije uvezena — isto pravilo koje važi za glavne mere. */
  previous: number | null;
}

export interface GroupRow {
  supergroup: string;
  group: string;
  /** Artikal čija putanja ne razrešava nadgrupu. Red se prikazuje, ne izostavlja. */
  unclassified: boolean;
  /** KILOGRAMI. Sve prodato u drugoj jedinici je u `otherUnits`. */
  quantity: Measure;
  otherUnits: UnitQuantity[];
  salesValue: Measure;
  ruc: Measure;
  avgSalesPrice: number;
  avgPurchasePrice: number;
  prevAvgSalesPrice: number;
  prevAvgPurchasePrice: number;
  /**
   * RUC po kilogramu — jedna mera, koju server izračuna i koju ekran, Excel i PDF samo čitaju.
   *
   * Razlika prodajne i nabavne cene NIJE ova mera: ona ne nosi troškove prodaje ni fakturisane
   * troškove, pa daje veći broj (avgust 2026: 13,8969 prema 11,6955). Ne izvoditi je ponovo.
   */
  rucPerKg: Ratio;
}

/**
 * Jedan artikal unutar jedne grupe — ono od čega je red grupe sačinjen.
 *
 * Šifra je identitet, naziv je natpis: mereno nad celim ledgerom to dvoje je jedan na jedan, pa
 * spuštanje po šifri ne može da spoji dva artikla ni da rascepi jedan.
 */
export interface ArticleRow {
  code: string;
  name: string;
  quantity: Measure;
  salesValue: Measure;
  ruc: Measure;
}

/** Artikli jedne grupe, sa grupom kojoj pripadaju — da zakasneo odgovor ne sleti pod tuđi red. */
export interface ArticleBreakdown {
  supergroup: string;
  group: string;
  articles: ArticleRow[];
}

/** Jedan kupac unutar zemlje ili knjige jednog komercijaliste. */
export interface CustomerRow {
  erpId: string;
  name: string;
  quantity: Measure;
  salesValue: Measure;
  ruc: Measure;
}

/** Kupci jedne zemlje ili jednog komercijaliste, sa opsegom za koji su traženi. */
export interface CustomerBreakdown {
  country: string | null;
  salesperson: string | null;
  customers: CustomerRow[];
}

/** Šta je jedan kupac kupio, unutar opsega kroz koji se sišlo. */
export interface CustomerArticles {
  erpCustomerId: string;
  country: string | null;
  salesperson: string | null;
  articles: ArticleRow[];
}

export interface CountryRow {
  name: string;
  code: string;
  quantity: Measure;
  /** Vrednost prodaje — ista mera koju nose nivoi kupca i artikla, da se kolone poklope. */
  salesValue: Measure;
  ruc: Measure;
  /**
   * Udeo u ukupnom za taj period, u procentima.
   *
   * Štampani izveštaj i ovu kolonu zove „Index", ali to je udeo a ne odnos prema prošloj godini —
   * kolone se sabiraju na 100. Zato su udeo i odnos ovde dva različita polja.
   */
  shareQuantity: number | null;
  shareRuc: number | null;
  prevShareQuantity: number | null;
  prevShareRuc: number | null;
}

/**
 * Red tabele komercijalista.
 *
 * Štampani izveštaj dva mala komercijalista ušiva u kućni red — aritmetika se poklapa do kilograma
 * i do dinara, ali po pravilu koje podaci ne nose. Ovde svaki ima svoj red, a zbir je isti.
 */
export interface SalespersonRow {
  name: string;
  quantity: Measure;
  salesValue: Measure;
  ruc: Measure;
  /**
   * Naplaćeno u periodu, po NAŠOJ raspodeli.
   *
   * Zbir je tačan, raspodela je naša: tri pravila su merena prema štampanom izveštaju i nijedno ga
   * ne reprodukuje — na jednom čoveku odstupanje je faktor 31. Ekran to kaže.
   */
  collected: Measure;
}

/**
 * Naplata razdvojena po GRANI obračuna — podela koju štampani izveštaj ne pokazuje.
 *
 * Četiri grane, avgust 2026: bankovni izvod 349 / 314.259.477,21, specifikacija 120 /
 * 230.557.029,70, kompenzacija 10 / 17.983.762,40 i interni nalog 2 / 6.782.604,56 — ukupno
 * 569.582.873,87, što je kontrolni izveštaj do dinara.
 */
export interface CollectionKind {
  /** Grana ERP-ovog obračuna iz koje red dolazi; zatvaranja avansa ovde nema, ono nije naplata. */
  kind: "IZVOD" | "SPECIFIKACIJA" | "KOMPENZACIJA" | "INTERNI_NALOG";
  amount: number;
  entries: number;
}

export type PeriodKind = "MONTH" | "QUARTER" | "YEAR" | "YTD" | "CUSTOM";

/** Koliko jednog raspona pokrivaju uvezeni podaci. */
export type Span = "FULL" | "PARTIAL" | "NONE";

/**
 * Prozor koji odgovor nosi, i gde u njemu stoje dva poređena raspona.
 *
 * `to` je dan do kog seže KOPIJA izvora, ne dan zadnjeg dokumenta. Danas se poklapaju — oba su
 * 22.09.2026 — i to je slučajnost: nedelju u kojoj niko ne fakturiše presek izveden iz podataka
 * tiho pomera unazad.
 */
export interface Coverage {
  from: string;
  to: string;
  /** `PARTIAL` znači da period prelazi ivicu prozora — brojke su prave ali nepune. */
  current: Span;
  /** `NONE` znači da prethodni period nije uvezen; tada poređenja nema, a ne da je nula. */
  previous: Span;
}

export interface ReportPeriodInfo {
  kind: PeriodKind;
  from: string;
  to: string;
  prevFrom: string;
  prevTo: string;
  /** Zamrznut mesec je konačan; dok nije, RUC se još može pomeriti pod izveštajem. */
  frozen: boolean;
  frozenAt: string | null;
  /** `null` znači NEPOZNATO, ne „sve": tada stranica ne imenuje presek ni ne tvrdi potpunost. */
  coverage: Coverage | null;
}

export interface SalesReport {
  period: ReportPeriodInfo;
  country: string | null;
  /** `quantity` su KILOGRAMI; `otherUnits` nosi sve van kilograma, imenovano a ne pribrojeno. */
  totals: {
    quantity: Measure;
    otherUnits: UnitQuantity[];
    salesValue: Measure;
    ruc: Measure;
    /** RUC po kilogramu nad kilogramskim stavkama; vidi `GroupRow.rucPerKg`. */
    rucPerKg: Ratio;
    rows: number;
  };
  groups: GroupRow[];
  countries: CountryRow[];
  salespeople: SalespersonRow[];
  collectionKinds: CollectionKind[];
  availableCountries: string[];
}

/** Šta se traži od servera za jedan period. Nedostajući deo server odbija, ne pretpostavlja. */
export interface PeriodQuery {
  kind: PeriodKind;
  year?: number;
  month?: number;
  quarter?: number;
  asOf?: string;
  from?: string;
  to?: string;
  country?: string;
}

function upit(q: PeriodQuery): string {
  const p = new URLSearchParams();
  p.set("kind", q.kind);
  if (q.year != null) p.set("year", String(q.year));
  if (q.month != null) p.set("month", String(q.month));
  if (q.quarter != null) p.set("quarter", String(q.quarter));
  if (q.asOf) p.set("asOf", q.asOf);
  if (q.from) p.set("from", q.from);
  if (q.to) p.set("to", q.to);
  if (q.country) p.set("country", q.country);
  return p.toString();
}


// ----------------------------------------------------------------- potraživanja

/** Deset kofa koje izveštaj seče. NISU granice ekrana finansija (7/30/60/120) — to je odluka. */
export type AgingBand =
  | "NOT_DUE_LATER"
  | "NOT_DUE_30"
  | "NOT_DUE_15"
  | "NOT_DUE_7"
  | "DUE_TODAY"
  | "OVERDUE_7"
  | "OVERDUE_30"
  | "OVERDUE_60"
  | "OVERDUE_90"
  | "OVERDUE_90_PLUS";

/** Redosled u kom se kofe čitaju i crtaju: od najdaljeg nedospelog do najstarijeg dospelog. */
export const AGING_BANDS: readonly AgingBand[] = [
  "NOT_DUE_LATER",
  "NOT_DUE_30",
  "NOT_DUE_15",
  "NOT_DUE_7",
  "DUE_TODAY",
  "OVERDUE_7",
  "OVERDUE_30",
  "OVERDUE_60",
  "OVERDUE_90",
  "OVERDUE_90_PLUS",
];

/**
 * Koji snimak je stvarno pročitan i koliko je to daleko od traženog dana.
 *
 * ERP-ov snimak ne nosi datum i ERP ga prepisuje, pa za period pre prvog uvoza nema šta da se
 * pokaže, a za period posle njega najbliži snimak može biti dan-dva pored. Ekran kaže koji je dobio.
 */
/**
 * Koji je dan — TRI datuma, svaki svoj, jer nisu isti.
 *
 * `actual` je dan na koji je ERP računao dospelost, `sourceCutoff` dan do kog kopija baze seže, a
 * `syncedAt` trenutak uvoza. Do 12.10.2026 je `actual` nosio datum uvoza, pa je ekran nad snimkom
 * starim dve nedelje pisao današnji dan.
 */
export interface SnapshotInfo {
  requested: string;
  actual: string | null;
  exact: boolean;
  daysOff: number;
  earliestHeld: string | null;
  sourceCutoff: string | null;
  syncedAt: string | null;
}

export interface AgingRow {
  currency: string;
  /** Sve deset kofa, nula uključena: kofe koje nema i kofe od nule čitaju se isto. */
  bands: Record<AgingBand, number>;
  overdue: number;
  notDue: number;
  total: number;
  customers: number;
}

/**
 * Jedna pozicija kupca.
 *
 * `balance` je saldo iz knjige — činjenica. `overdue` i `overdue90Plus` izlaze iz ERP-ove raspodele
 * tog salda po kofama, dakle procena, jer veze uplate i računa u ERP-u nema. `disputed` je naše.
 */
export interface CustomerPosition {
  companyId: string | null;
  erpCustomerId: string;
  name: string;
  customerGroup: string;
  country: string;
  salesperson: string;
  currency: string;
  balance: number;
  overdue: number;
  overdue90Plus: number;
  disputed: number;
  oldestDays: number | null;
}

/** Odsečen spisak koji kaže da je odsečen i koliko je ostatak. */
/**
 * Odsečen spisak dužnika — JEDNA LISTA, JEDNA VALUTA.
 *
 * Rang preko valuta poredi brojeve a ne novac: 601.135,70 EUR je stajalo ispod 56.111.328 RSD, pa
 * inostrani kupac nikad nije mogao da se pojavi među najvećima, a `restAmount` je sabirao dinare
 * sa evrima. Štampani original razdvaja isto tako.
 */
export interface TopList {
  currency: string;
  rows: CustomerPosition[];
  shown: number;
  total: number;
  restAmount: number;
}

/**
 * Jedan kupac unutar jedne kofe starosti.
 *
 * `amount` je ono što nosi U TOJ KOFI i vodi; `balance` je njegov ceo saldo i stoji pored. Dva
 * različita pitanja — spuštanje koje bi pokazalo samo saldo odgovorilo bi na ono koje niko nije
 * postavio dok gleda kofu.
 */
export interface BandCustomer {
  companyId: string | null;
  erpCustomerId: string;
  name: string;
  country: string;
  salesperson: string;
  amount: number;
  balance: number;
  oldestDays: number | null;
}

/** Kupci u jednoj kofi, odsečeni na dužinu — i spisak to kaže. */
export interface BandCustomers {
  scope: string;
  currency: string;
  band: string;
  rows: BandCustomer[];
  shown: number;
  total: number;
  restAmount: number;
}

export interface AdvanceRow {
  currency: string;
  amount: number;
  customers: number;
  /** Broj konta ugašenog 01.01.2021, ako je na njemu nešto ostalo. */
  retiredAccount: string | null;
  retiredAmount: number;
}

export interface CountryExposure {
  country: string;
  currency: string;
  balance: number;
  overdue: number;
  customers: number;
}

export interface BalancePoint {
  year: number;
  month: number;
  closingBalance: number;
  /** The month's turnover, without the balance carried in from the year before. */
  netChange: number;
  /** Carried in from the year before; null for a month imported before it had its own column. */
  opening: number | null;
}

export interface DisputeTotal {
  currency: string;
  amount: number;
  marks: number;
}

export interface Dispute {
  id: string;
  companyId: string;
  companyName: string | null;
  currency: string;
  amount: number;
  reason: string;
  openedOn: string;
  resolvedOn: string | null;
  resolution: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface RiskNote {
  id: string;
  companyId: string;
  companyName: string | null;
  notedOn: string;
  note: string;
}

export interface ReceivablesReport {
  snapshot: SnapshotInfo;
  domestic: AgingRow[];
  foreign: AgingRow[];
  advancesReceived: AdvanceRow[];
  advancesGiven: AdvanceRow[];
  topDebtors: TopList[];
  topForeignDebtors: TopList[];
  countries: CountryExposure[];
  balanceSeries: BalancePoint[];
  disputed: DisputeTotal[];
  riskNotes: RiskNote[];
}

export interface DisputeRequest {
  companyId: string;
  currency: string;
  amount: number;
  reason: string;
  openedOn: string;
  resolvedOn?: string | null;
  resolution?: string | null;
}

export interface RiskNoteRequest {
  companyId: string;
  notedOn: string;
  note: string;
}

/** Strana sa servera; ekran traži stranu i pokazuje stanje učitavanja. */
export interface Strana<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

// ----------------------------------------------------------------- proizvodnja

/**
 * Jedan red tabele pogona. `value` je null tamo gde izveštaj daje samo količinu.
 *
 * `unclassified` označava red čiji artikal ne razrešava grupu; ekran ga imenuje, nikad ne ispušta.
 */
/**
 * Količina u jedinici koja nije kilogram — imenovana, nikad pribrojena tonaži.
 *
 * Proizvodnja ne poredi periode, pa nosi JEDAN broj, a ne meru sa prethodnim.
 */
export interface ProductionUnitQuantity {
  unit: string;
  quantity: number;
}

/**
 * Jedan red tabele pogona. `value` je null tamo gde izveštaj daje samo količinu.
 *
 * `quantity` su KILOGRAMI. `otherUnits` nosi ono što je išlo u drugoj jedinici, i ekran to ispisuje
 * POred brojke a ne u njoj: 174,6170 komada enzima sabrano sa 2.780,8583 kilograma daje broj čiju
 * netačnost čitalac ne može da vidi.
 */
export interface PlantLine {
  group: string;
  unclassified: boolean;
  quantity: number;
  value: number | null;
  otherUnits: ProductionUnitQuantity[];
}

/**
 * Jedna tabela pod pogonom.
 *
 * `direction` je OZNAKA, ne natpis: CONSUMED, AUXILIARY ili PRODUCED. Prevodi je onaj ko crta, na
 * svom jeziku — natpis razrešen na serveru pratio je jezik HTTP zaglavlja, pa je PDF izlazio sa
 * srpskim naslovima nad engleskim tabelama.
 *
 * `printedCodeSum` nije null u JEDINOJ tabeli u kojoj štampani izveštaj u koloni „Vrednost,rsd"
 * nosi zbir stare šifre artikla a ne vrednost. Kolone vrednosti ovde uvek nose vrednost, pa se
 * kolona sabira; štampana brojka putuje pored, da se naša strana može uporediti sa onom koja se
 * godinama čita.
 */
export interface PlantTable {
  direction: "CONSUMED" | "AUXILIARY" | "PRODUCED";
  lines: PlantLine[];
  totalQuantity: number;
  totalValue: number | null;
  printedCodeSum: number | null;
  /** Zbir tabele van kilograma, sabran PO JEDINICI i nikad međusobno. */
  otherUnits: ProductionUnitQuantity[];
}

/**
 * Brojka uz pogon. `kind` je oznaka — CONSUMED ili PRODUCED.
 *
 * `value` su KILOGRAMI; `otherUnits` je ono što je isti smer imao u drugoj jedinici.
 */
export interface PlantFigure {
  kind: "CONSUMED" | "PRODUCED";
  value: number;
  otherUnits: ProductionUnitQuantity[];
}

/**
 * Jedan pogon.
 *
 * `name` je vlastito ime iz štampanog izveštaja i ne prevodi se. `inPrintedReport` je netačno za
 * dva koja štampani izveštaj ne pokazuje; ekran to kaže svojim jezikom.
 */
export interface Plant {
  code: string;
  name: string;
  inPrintedReport: boolean;
  tables: PlantTable[];
  figures: PlantFigure[];
  /**
   * Šta je pogon PROIZVEO, i ništa drugo.
   *
   * Ni ekran ni dokument je ne računaju: dok je ekran sabirao smerove a PDF ispuštao izlaznu
   * tabelu, jedan pogon je imao tri odgovora (9.160.267 / 4.765.567 / 4.394.700 kg).
   */
  producedQuantity: number;
  /**
   * Šta je pogon proizveo van kilograma.
   *
   * Danas nijedan red — svaka proizvodna količina u komadima je utrošak — ali brojka koja ne ume da
   * izrazi drugu jedinicu je isti kvar kao tonaža koja je u sebe uzme.
   */
  producedOtherUnits: ProductionUnitQuantity[];
}

/** Podela prodatog sojinog brašna po fabrici koja ga je napravila. */
export interface FlourSplitRow {
  plant: string;
  quantity: number;
  prevQuantity: number;
  salesValue: number;
  prevSalesValue: number;
  ruc: number;
  prevRuc: number;
}

/**
 * Bilans jednog pogona: šta je napravio prema onome što je izašlo.
 *
 * `consumedElsewhere` je **null kad se ne može pripisati**, ne nula: proizvodnja nosi grupu a ne
 * artikal, pa kad istu grupu prave dva pogona utrošak u trećem se ne može podeliti. Nula bi
 * tvrdila da u drugom pogonu nije utrošeno ništa — druga tvrdnja.
 */
export interface PlantBalanceRow {
  plant: string;
  plantName: string;
  /** Grupa, ili prazno kad je red zbir celog pogona. */
  articleGroup: string;
  produced: number;
  sold: number;
  consumedElsewhere: number | null;
  totalOut: number;
}

export interface ProductionReport {
  from: string;
  to: string;
  /** Prethodni period, imenovan u odgovoru — ograda mora da kaže KOJI period nije uvezen. */
  prevFrom: string;
  prevTo: string;
  /**
   * Zemlja za koju je merena PODELA BRAŠNA, ili null za sve.
   *
   * Na jednoj strani žive dve različite mere: podela brašna je prodajna i nosi zemlju, a tabele
   * pogona su proizvodne i zemlju NEMAJU. Zato obuhvat stoji uz tabelu, ne samo u filteru.
   */
  country: string | null;
  plants: Plant[];
  flourSplit: FlourSplitRow[];
  /** Ono što podela ne pokriva — imenovano, a ne ostavljeno kao rupa u računu. */
  flourRemainder: FlourSplitRow | null;
  /** Bilans po pogonu: proizvedeno prema izašlom. */
  plantBalance: PlantBalanceRow[];
  /** Stara fabrika po GRUPI, kako original i nosi. */
  oldPlantByGroup: PlantBalanceRow[];
  /** Prodato u kilogramima koje nijedan pogon ne može da prisvoji — i trgovačka roba. */
  soldWithoutPlant: number;
  /** Šta još nije reprodukovano iz ERP-a; ekran to kaže umesto da prikaže prazno. */
  notMeasured: string[];
  /**
   * Gde period stoji unutar podataka koje portal drži, nad OBA skupa — proizvodnjom i ledgerom.
   *
   * Prodajna strana je ovo nosila od početka, proizvodna nije, pa je isti nepun period na jednoj
   * strani bio imenovan a na drugoj prećutan.
   */
  coverage: Coverage | null;
}

export const reportsApi = {
  sales: (q: PeriodQuery) => apiFetch<SalesReport>(`/reports/sales?${upit(q)}`),
  /**
   * Artikli jedne grupe. Grupa se šalje TAČNO onako kako je red grupe nosi, prazan string
   * uključen: artikal koji ne razrešava nadgrupu je red ove tabele kao i svaki drugi.
   */
  salesArticles: (q: PeriodQuery, supergroup: string, group: string) =>
    apiFetch<ArticleBreakdown>(
      `/reports/sales/articles?${upit(q)}&supergroup=${encodeURIComponent(supergroup)}&group=${encodeURIComponent(group)}`,
    ),
  /**
   * Kupci jedne zemlje ili jednog komercijaliste.
   *
   * Oba opsega putuju u upit. Spisak kupaca koji zanemari zemlju iz koje je otvoren odgovara na
   * pitanje koje niko nije postavio — i izgleda uverljivo.
   */
  salesCustomers: (q: PeriodQuery, country?: string | null, salesperson?: string | null) =>
    apiFetch<CustomerBreakdown>(
      // PRAZAN STRING SE ŠALJE. Red bez zemlje („/") nosi prazno ime, i server razlikuje
      // „parametra nema" od „parametar je prazan" — dok je ovde stajalo `country ?`, prazno je
      // ispadalo iz adrese i spuštanje sa tog reda vraćalo je ceo period.
      `/reports/sales/customers?${upit(q)}` +
        (country != null ? `&country=${encodeURIComponent(country)}` : "") +
        (salesperson != null ? `&salesperson=${encodeURIComponent(salesperson)}` : ""),
    ),
  /** Artikli jednog kupca, u istom opsegu kroz koji se sišlo do njega. */
  salesCustomerArticles: (
    q: PeriodQuery,
    customer: string,
    country?: string | null,
    salesperson?: string | null,
  ) =>
    apiFetch<CustomerArticles>(
      `/reports/sales/customer-articles?${upit(q)}&customer=${encodeURIComponent(customer)}` +
        (country != null ? `&country=${encodeURIComponent(country)}` : "") +
        (salesperson != null ? `&salesperson=${encodeURIComponent(salesperson)}` : ""),
    ),
  /** Potraživanja ne primaju filter zemlje: taj filter pripada prodajnom ledgeru, ne knjizi. */
  /** Kupci u jednoj kofi starosti; opseg je konto (DOMESTIC/FOREIGN), valuta i kofa. */
  receivableBandCustomers: (q: PeriodQuery, scope: "DOMESTIC" | "FOREIGN", currency: string, band: string) =>
    apiFetch<BandCustomers>(
      `/reports/receivables/band-customers?${upit({ ...q, country: undefined })}` +
        `&scope=${scope}&currency=${encodeURIComponent(currency)}&band=${band}`,
    ),
  receivables: (q: PeriodQuery) =>
    apiFetch<ReceivablesReport>(`/reports/receivables?${upit({ ...q, country: undefined })}`),
  /**
   * Proizvodnja NOSI zemlju, i to namerno.
   *
   * Zemlja je do 13.10.2026 bila izbacivana iz upita, pa je za `country=SRBIJA` prodaja davala
   * 208.025 kg brašna a podela po fabrikama nefiltriranih 704.565 — dva broja o istom brašnu na
   * istoj strani. Server je filtrira samo na podeli brašna; tabele pogona je ne poznaju i odgovor
   * nosi zemlju natrag, pa ekran imenuje obuhvat svake tabele.
   */
  production: (q: PeriodQuery) => apiFetch<ProductionReport>(`/reports/production?${upit(q)}`),
  disputes: (page: number) => apiFetch<Strana<Dispute>>(`/reports/receivables/disputes?page=${page}`),
  createDispute: (body: DisputeRequest) =>
    apiFetch<Dispute>("/reports/receivables/disputes", { method: "POST", body }),
  updateDispute: (id: string, body: DisputeRequest) =>
    apiFetch<Dispute>(`/reports/receivables/disputes/${id}`, { method: "PUT", body }),
  deleteDispute: (id: string) =>
    apiFetch<void>(`/reports/receivables/disputes/${id}`, { method: "DELETE" }),
  riskNotes: (page: number) => apiFetch<Strana<RiskNote>>(`/reports/receivables/risk-notes?page=${page}`),
  createRiskNote: (body: RiskNoteRequest) =>
    apiFetch<RiskNote>("/reports/receivables/risk-notes", { method: "POST", body }),
  updateRiskNote: (id: string, body: RiskNoteRequest) =>
    apiFetch<RiskNote>(`/reports/receivables/risk-notes/${id}`, { method: "PUT", body }),
  deleteRiskNote: (id: string) =>
    apiFetch<void>(`/reports/receivables/risk-notes/${id}`, { method: "DELETE" }),
};
